import * as React from "react";

export interface ResumeRequestProps {
  profile_id: number;
  requested: boolean;
  authenticity_token: string;
}

export default function ResumeRequest({
  profile_id,
  requested,
  authenticity_token,
}: ResumeRequestProps) {
  const [state, setState] = React.useState<{
    requested: boolean;
    loading: boolean;
    error: string | null;
  }>({ requested, loading: false, error: null });

  const sendButtonOnClick = (event?: React.MouseEvent) => {
    event?.preventDefault();
    setState((prev) => ({ ...prev, loading: true, error: null }));
    sendRequest(profile_id, authenticity_token).then((outcome) => {
      if (outcome.ok) {
        setState((prev) => ({ ...prev, requested: true, loading: false }));
      } else {
        setState((prev) => ({ ...prev, loading: false, error: outcome.error || GENERIC_ERROR }));
      }
    });
  };

  return (
    <div className="card shadow-sm profile-card-design mt-4">
      <div className="card-header profile-cards-head">
        <i className="fas fa-book"></i> Resume & Work Experience
      </div>
      <div className="padding-right mb-20">
        <div className="text-center">
          {state.error && !state.loading && <RequestError message={state.error} />}
          {!state.requested && !state.loading && !state.error && (
            <RequestForm sendButtonOnClick={sendButtonOnClick} />
          )}
          {state.requested && !state.loading && <RequestedMessage />}
          {state.loading && (
            <>Loading...</>
          )}
        </div>
      </div>
    </div>
  );
}

function RequestError({ message }: { message: string }) {
  return (
    <div className="pt-3 pb-4">
      <p role="alert">{message}</p>
    </div>
  );
}

function RequestedMessage() {
  return (
    <div className="pt-3 pb-4">
      <p>Thank you for your interest in this candidate. Your request has been
        sent.</p>
      <a href="/profiles" className="btn btn-primary btn-rounded">
        <i className="fas fa-search"></i> Search for More Candidates
      </a>
    </div>
  );
}

function RequestForm({ sendButtonOnClick }) {
  return (
    <div className="pt-3 pb-4">
      <p>This user hasn't uploaded their resume or experience.</p>
      <a href="#resume_request" onClick={sendButtonOnClick} className="btn btn-primary btn-rounded">
        <i className="fas fa-envelope"></i> Request Resume & Working Experience
      </a>
    </div>
  );
}

const GENERIC_ERROR = "We could not send your request. Please try again.";

type SendOutcome = { ok: boolean; error?: string };

// Only a 2xx counts as sent. A refusal (for example 422 with code
// "rpo_managed" for a recruiter-managed candidate) shows the server's message.
export function sendRequest(
  profile_id: number,
  authenticity_token: string
): Promise<SendOutcome> {
  const url = `/resume_requests.json`;

  const options: object = {
    method: "POST",
    redirect: "manual",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticity_token,
    },
    body: JSON.stringify({ profile_id }),
  };

  return fetch(url, options)
    .then(async (result): Promise<SendOutcome> => {
      if (result.ok) return { ok: true };

      const body = await result.json().catch(() => null);
      const error =
        body && typeof body.error === "string" && body.error.trim() !== ""
          ? body.error
          : GENERIC_ERROR;
      return { ok: false, error };
    })
    .catch((error): SendOutcome => {
      console.error(error);
      return { ok: false, error: GENERIC_ERROR };
    });
}
